<template>
  <div class="maker-cart-hero-section">
    <div class="hero-container">
      <div class="hero-text-content">
        <h1 class="hero-title">
          <span
            v-for="(line, index) in activeSlide.titleLines"
            :key="index"
            class="hero-title__line"
          >
            {{ line }}
          </span>
        </h1>

        <div class="hero-description-section">
          <p class="hero-description">
            {{ activeSlide.description }}
          </p>

          <ActionButton
            variant="primary"
            size="large"
            type="button"
            customClass="hero-cta"
            @click="goToActiveRoute"
          >
            {{ activeSlide.buttonText }}
            <template #icon-right>
              <ArrowShortRightIcon color="currentColor" />
            </template>
          </ActionButton>
        </div>
      </div>

      <div class="hero-mobile-nav">
        <button
          type="button"
          class="carousel-nav-btn"
          @click="previousSlide"
          aria-label="Previous product"
        >
          <ChevronLeftIcon color="currentColor" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="carousel-nav-btn"
          @click="nextSlide"
          aria-label="Next product"
        >
          <ChevronRightIcon color="currentColor" aria-hidden="true" />
        </button>
      </div>

      <div class="hero-visual-content">
        <div class="hero-main-image">
          <img
            :key="activeSlide.id"
            :src="activeSlide.image"
            :alt="activeSlide.imageAlt"
            class="main-image"
          />
        </div>

        <div class="hero-carousel-section">
          <button
            type="button"
            class="carousel-nav-btn carousel-nav-left"
            @click="previousSlide"
            aria-label="Previous product"
          >
            <ChevronLeftIcon color="currentColor" aria-hidden="true" />
          </button>

          <div class="preview-container">
            <button
              v-for="(slide, index) in slides"
              :key="slide.id"
              type="button"
              class="preview-slide"
              :class="{ active: index === currentSlide }"
              :aria-current="index === currentSlide ? 'true' : undefined"
              :aria-label="`Show ${slide.thumbTitle}`"
              @click="goToSlide(index)"
            >
              <span class="preview-card">
                <img
                  :src="slide.image"
                  :alt="slide.thumbTitle"
                  class="preview-image"
                />
                <span class="preview-title">{{ slide.thumbTitle }}</span>
              </span>
            </button>
          </div>

          <button
            type="button"
            class="carousel-nav-btn carousel-nav-right"
            @click="nextSlide"
            aria-label="Next product"
          >
            <ChevronRightIcon color="currentColor" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";
import { ArrowShortRightIcon } from "@/components/icons";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

const router = useRouter();
const currentSlide = ref(0);

const slides = [
  {
    id: "makercart",
    titleLines: ["Your Classroom, Future-Ready", "— With Makercart"],
    description:
      "Hands-on STEM learning for under $10 per student: Our flagship mobile makerspace brings together materials, classroom-ready activities, and educator resources—supporting up to 80 students.",
    buttonText: "Explore Makercart",
    image: "/images/maker-cart/get-started/makercart.png",
    imageAlt: "Makercart filled with organized classroom supplies",
    thumbTitle: "Makercart",
    route: { name: "MakerCartPage" },
  },
  {
    id: "signature",
    titleLines: ["A Makercart Built Around What You Want to Teach"],
    description:
      "Choose from curated Signature Makercarts designed around TrendX, Curriculum Connect and CTE Pathways—each with four selected activities and the materials and resources to implement them.",
    buttonText: "Explore Signature Makercarts",
    image: "/images/maker-cart/get-started/customized-makercart.png",
    imageAlt: "Signature Makercart with curated classroom supplies",
    thumbTitle: "Signature Makercarts",
    route: { name: "MakerCartPage", query: { tab: "signature" } },
  },
  {
    id: "makercrate",
    titleLines: ["Big Making Can Start Small"],
    description:
      "Bring hands-on learning into your classroom with focused, ready-to-use activity kits for up to 20 students—starting at $299.",
    buttonText: "Explore Makercrate",
    image: "/images/maker-cart/get-started/inventor-kit-box-full.png",
    imageAlt: "Makercrate Inventor Kit with classroom materials",
    thumbTitle: "Makercrate",
    route: { name: "MakerCartPage", query: { tab: "makercrate" } },
  },
];

const activeSlide = computed(() => slides[currentSlide.value] || slides[0]);

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.length;
};

const previousSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + slides.length) % slides.length;
};

const goToSlide = (index) => {
  currentSlide.value = index;
};

const goToActiveRoute = () => {
  router.push(activeSlide.value.route);
};
</script>

<style scoped>
.maker-cart-hero-section {
  min-height: 724px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white, #ffffff);
  padding: 40px 0;
}

.hero-container {
  display: flex;
  gap: 64px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.hero-text-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1 1 548px;
  max-width: 548px;
}

.hero-title {
  display: flex;
  flex-direction: column;
  margin: 0;
  font-family: var(--font-family-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-display-large);
  line-height: var(--line-height-display-large);
  letter-spacing: -0.1425px;
  color: var(--blue-dark-2, #061f32);
  text-align: center;
}

.hero-title__line {
  display: block;
}

.hero-description-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
}

.hero-description {
  margin: 0;
  width: 100%;
  max-width: 522px;
  font-family: var(--font-family-primary);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-headline-small);
  line-height: var(--line-height-headline-small);
  color: var(--neutral-700, #6c737f);
  text-align: center;
}

:deep(.hero-cta) {
  white-space: nowrap;
}

.hero-visual-content {
  flex: 0 0 668px;
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: center;
  justify-content: center;
}

.hero-main-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 414px;
  height: 414px;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hero-carousel-section {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.carousel-nav-btn {
  width: 48px;
  height: 48px;
  border: 1px solid var(--primary-blue, #003d6a);
  border-radius: 8px;
  background-color: transparent;
  color: var(--primary-blue, #003d6a);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.carousel-nav-btn:hover {
  background-color: var(--primary-blue, #003d6a);
  color: white;
}

.carousel-nav-btn:focus-visible,
.preview-slide:focus-visible {
  outline: 2px solid var(--primary-blue, #003d6a);
  outline-offset: 2px;
}

.preview-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.preview-slide {
  width: 180px;
  height: 180px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.preview-card {
  width: 100%;
  height: 100%;
  background: var(--white-hover-1, #f2f3fb);
  border-radius: 12px;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 16px 12px;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.preview-slide.active .preview-card {
  border-color: var(--primary-blue, #003d6a);
  background: var(--badge-blue-light-1, #e6ecf0);
}

.preview-image {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.preview-title {
  font-family: var(--font-family-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-title-small);
  line-height: var(--line-height-title-small);
  letter-spacing: 0.014px;
  color: var(--blue-dark-2, #061f32);
  text-align: center;
}

.hero-mobile-nav {
  display: none;
}

@media (max-width: 1200px) {
  .hero-container {
    gap: 40px;
  }

  .hero-text-content {
    flex-basis: 450px;
  }

  .hero-visual-content {
    flex-basis: 550px;
  }

  .hero-title {
    font-size: var(--font-size-display-medium);
    line-height: var(--line-height-display-medium);
  }

  .hero-main-image {
    width: 320px;
    height: 320px;
  }

  .preview-slide {
    width: 148px;
    height: 148px;
  }

  .preview-image {
    width: 80px;
    height: 80px;
  }
}

@media (max-width: 992px) {
  .hero-container {
    flex-direction: column-reverse;
  }

  .hero-text-content,
  .hero-visual-content {
    flex: none;
    width: 100%;
    max-width: 668px;
  }

  .hero-text-content {
    gap: 24px;
  }

  .hero-title {
    font-size: 42px;
    line-height: 48px;
  }
}

@media (max-width: 768px) {
  .maker-cart-hero-section {
    padding: 16px 0;
    min-height: 0;
  }

  .hero-container {
    flex-direction: column;
    gap: 32px;
    align-items: center;
  }

  .hero-visual-content {
    order: -1;
    flex: none;
    width: 100%;
    gap: 0;
  }

  .hero-visual-content .hero-carousel-section {
    display: none;
  }

  .hero-text-content {
    flex: none;
    width: 100%;
    max-width: 361px;
    gap: 16px;
  }

  .hero-title {
    font-size: var(--font-size-headline-medium);
    line-height: 1;
  }

  .hero-description-section {
    gap: 16px;
    padding: 0 13px;
  }

  .hero-description {
    max-width: none;
    font-size: var(--font-size-body-medium);
    line-height: var(--line-height-body-medium);
    font-weight: var(--font-weight-medium);
    letter-spacing: 0.035px;
  }

  .hero-main-image {
    width: 150px;
    height: 150px;
  }

  .hero-mobile-nav {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
  }

  .hero-mobile-nav .carousel-nav-btn {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }

  .hero-mobile-nav .carousel-nav-btn :deep(svg) {
    width: 20px;
    height: 20px;
  }

  :deep(.hero-cta) {
    min-height: 40px;
    height: 40px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: var(--font-size-title-small);
    white-space: nowrap;
  }

  :deep(.hero-cta .action-button__text) {
    font-size: var(--font-size-title-small);
    line-height: var(--line-height-title-small);
    letter-spacing: 0.014px;
  }

  :deep(.hero-cta .action-button__icon svg) {
    width: 20px;
    height: 20px;
  }
}
</style>
